// shell/RightRail/context.ts — Outlet-context types + helpers for page-fed right-rail content.
//
// ShellLayout provides { setRightRail, setWideCenter } via <Outlet context>. A routed page calls
// useRightRail(node) to publish its right-rail content (and clears it on unmount), and
// useWideCenter(true) to borrow the right column's width for itself.

import { createContext, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useOutletContext } from 'react-router-dom';

/** Optional slot inside a rail/drawer header; standalone rails render their own header. */
export const RailToolbarContext = createContext<HTMLElement | null>(null);

export interface ShellContext {
  setRightRail: (node: ReactNode | null) => void;
  /** While true the center column also takes the right column's width (see useWideCenter). */
  setWideCenter: (wide: boolean) => void;
}

export function useShellContext(): ShellContext {
  return useOutletContext<ShellContext>();
}

/**
 * Publish a node into the shell's right rail for the lifetime of the calling page.
 * Pass a STABLE node (defined outside render or memoized) to avoid redundant updates.
 */
export function useRightRail(node: ReactNode): void {
  const { setRightRail } = useShellContext();
  useEffect(() => {
    setRightRail(node);
    return () => setRightRail(null);
  }, [setRightRail, node]);
}

/**
 * While `wide`, the shell hands the right column's width to the center column and hides the
 * rail (the video watch page's theater mode). Released when `wide` turns false or the page
 * unmounts, so it never outlives the page that asked. Unlike collapsing the rail, this is
 * not saved: the next page gets its rail back.
 */
export function useWideCenter(wide: boolean): void {
  const { setWideCenter } = useShellContext();
  useEffect(() => {
    if (!wide) return;
    setWideCenter(true);
    return () => setWideCenter(false);
  }, [setWideCenter, wide]);
}
